import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalculatorIcon } from 'lucide-react';
const CALC_CSS = 'https://brokerbuddie.s3.ap-southeast-2.amazonaws.com/brokertimes-assets/Home+Loan+Borrowing+Power+Calculator/v2.1/css/calculator.css';
const CALC_JS = 'https://brokerbuddie.s3.ap-southeast-2.amazonaws.com/brokertimes-assets/Home+Loan+Borrowing+Power+Calculator/v2.1/js/calculator-embeddable.js';

// Settings handed to the third-party embed, matched to the brand palette so the
// calculator doesn't read as a bolted-on widget.
const CALC_CONFIG = {
  primaryColor: '#42b9a5',
  secondaryColor: '#42b9a5',
  ctaText: 'Learn More',
  ctaLink: 'https://calendly.com/liam-scalefinancestrategies/30min',
  lenderBuffer: 3,
  fontFamily: "'Inter', sans-serif",
  fontColor: '#545454',
  backgroundColor: '#faf8eb',
  borderColor: '#42b9a5'
};

// The embed wraps all of its setup in a `DOMContentLoaded` listener. In a
// single-page app that event fired long before this page is ever visited, so
// the listener would never run and the calculator would silently never appear.
// We re-dispatch the event once the script is in place to kick it off.
//
// Tracked at module scope: the script only ever needs to execute once per
// session. Re-appending it on every visit would register a duplicate listener
// each time and render the calculator multiple times over.
let scriptLoaded = false;
export function CalculatorPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const globals = window as unknown as Record<string, unknown>;
    // The embed reads its settings off this global as it initialises, so the
    // config must exist before the script runs.
    globals.tbtCalcConfig = CALC_CONFIG;
    if (!document.head.querySelector(`link[href="${CALC_CSS}"]`)) {
      const styleEl = document.createElement('link');
      styleEl.rel = 'stylesheet';
      styleEl.type = 'text/css';
      styleEl.href = CALC_CSS;
      document.head.appendChild(styleEl);
    }
    const boot = () => {
      document.dispatchEvent(new Event('DOMContentLoaded', {
        bubbles: true,
        cancelable: true
      }));
    };
    if (scriptLoaded) {
      // Script already executed this session — its listener is still attached,
      // so it just needs another event to rebuild into the empty container.
      boot();
    } else {
      const script = document.createElement('script');
      script.src = CALC_JS;
      script.async = true;
      script.onload = () => {
        scriptLoaded = true;
        boot();
      };
      document.body.appendChild(script);
    }
    const container = containerRef.current;
    return () => {
      // Empty the container on navigate so returning here rebuilds a single
      // clean instance rather than stacking a second calculator.
      if (container) container.innerHTML = '';
    };
  }, []);
  return <main className="pt-32 pb-24 bg-[#f8f9fb] dark:bg-[#0f1629] transition-colors duration-300" data-id="element-489" data-mp="1j3oj8io2vdpi">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-490" data-mp="33ntohswkz1wv">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-491" data-mp="2yogvsrj130md">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none mb-6" data-id="element-492" data-mp="2ylm9svkd97qu">
            <CalculatorIcon className="w-4 h-4 text-primary-500" data-id="element-493" data-mp="38kabukvyhc8w" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300" data-id="element-494" data-mp="3mci88bjbv172">
              Free Tool
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-5 leading-[1.15]" style={{
          fontWeight: "800",
          fontFamily: "Inter, sans-serif"
        }} data-id="element-495" data-mp="3idr9jzz7rbhu">
            Borrowing Power{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500" data-id="element-496" data-mp="21wn2birlw9zp">
              Calculator
            </span>
          </h1>

          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed font-light" data-id="element-497" data-mp="1yfist605645q">
            Get a quick estimate of what you may be able to borrow. Enter your
            income, expenses and commitments to see an indicative figure — then
            talk to us about structuring it properly.
          </p>
        </div>

        {/* Calculator embed */}
        <div className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white shadow-xl shadow-slate-900/5 dark:shadow-none overflow-hidden p-4 sm:p-6" data-id="element-498" data-mp="2daf8236dbrsb">
          <div id="tbt-calc-container" ref={containerRef} data-id="element-499" data-mp="1kom25kprzx1b" />
        </div>

        {/* Disclaimer */}
        <p className="mt-6 text-xs text-slate-400 dark:text-slate-500 text-center max-w-3xl mx-auto leading-relaxed" data-id="element-500" data-mp="1plmay3e0fdw4">
          This calculator provides an estimate only and is not an offer of
          finance or a pre-approval. Results are general in nature, do not
          consider your personal circumstances, and may differ from a lender's
          own assessment. Speak with us for advice tailored to your situation.
        </p>

        {/* Next tool — someone already thinking about numbers is the right
            person to offer the savings roadmap to. */}
        <Link to="/roadmap" className="mt-8 flex items-center justify-between gap-4 rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm dark:shadow-none px-6 py-5 hover:border-primary-400 dark:hover:border-primary-500/50 transition-colors group" data-id="element-501" data-mp="366eoyxubacvi">
          <span data-id="element-502" data-mp="2rhi9ifpxdxkv">
            <span className="block text-base font-semibold text-slate-900 dark:text-white" data-id="element-503" data-mp="2rh0e1lb37ne0">
              Work out your savings target
            </span>
            <span className="block mt-1 text-sm text-slate-500 dark:text-slate-400" data-id="element-504" data-mp="2ii55w3ohg382">
              Build a savings roadmap and see how you're tracking.
            </span>
          </span>
          <ArrowRightIcon className="w-5 h-5 flex-shrink-0 text-primary-500 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-505" data-mp="28jpj2cwhng4d" />
        </Link>

        {/* CTA */}
        <div className="mt-14 text-center rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm dark:shadow-none px-6 py-12" data-id="element-506" data-mp="23uwscw7pblsx">
          
          <p className="text-slate-600 dark:text-slate-300 max-w-xl mx-auto mb-8 font-light" data-id="element-507" data-mp="1pwyedrxalrdy">
            Lenders assess borrowing capacity differently, and the right
            structure can change what's possible. Let's map it out together.
          </p>
          <Link to="/#contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold text-lg hover:shadow-[0_0_30px_rgba(20,184,166,0.35)] transition-all group hover:scale-[1.02]" data-id="element-508" data-mp="2mlny7nshcu2k">
            Book a Strategy Session
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-509" data-mp="30kdvb3fautqx" />
          </Link>
        </div>
      </div>
    </main>;
}